import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import {
	calculateGrowth,
	COLOR_PALETTE1,
	darkblueColor,
	darkRedColor,
	growthGreenColor,
	lightBlueColor,
	midBlueColor,
	midMaroonColor,
	midOrangeColor,
	midRedColor,
} from "components/Reports/data";
import { dummyMetricsData, metricsDataAdapter, tableRowTypes } from "./data";
import parseErrMsgFromError from "utils/errorFunctions/parseErrorMessageFromError";
import { GlobalState } from "context/GlobalContext";
import CardSkeleton from "./CardSkeleton";
import { roundDecimalValueUptoTwo } from "utils/roundDecimalValue";
import { ArrowUpwardRounded } from "@mui/icons-material";
import { grey } from "@mui/material/colors";
import { fastapiVmInstance } from "utils/AxiosInstances/RareboxFastApi";
import { formatFixedOneNotation } from "../StoreListCards/data";
// --- Helper to format numbers ---
const formatNumber = (num) => {
	if (num >= 1e7) return roundDecimalValueUptoTwo(num / 1e7) + " Cr";
	if (num >= 1e5) return roundDecimalValueUptoTwo(num / 1e5).toFixed(2) + " L";
	if (num >= 1e3) return roundDecimalValueUptoTwo(num / 1e3).toFixed(1) + " K";
	return roundDecimalValueUptoTwo(num);
};

const MonthwiseBusinessMetrics = ({ filter, width = 900, height = 450 }) => {
	const { setSnackBarOpen, setSnackBar, snackbar } = GlobalState();

	const svgRef = useRef();
	const [resData, setResData] = useState(metricsDataAdapter(dummyMetricsData));
	const [loading, setLoading] = useState(false);

	const [xScale, setXScale] = useState(null);
	const [formattedData, setFormattedData] = useState([]);
	const margin = { top: 20, right: 90, bottom: 0, left: 110 };
	const parameters = ["UPT", "ASP", "AOV"];

	const uptColor = midBlueColor
	const aspColor = midOrangeColor
	const aovColor = midMaroonColor
	// const tyRevColor = darkblueColor
	// const lyRevColor = lightBlueColor
	const tyRevColor = COLOR_PALETTE1.slateGray
	const lyRevColor = COLOR_PALETTE1.beige

	const fetch = async () => {
		try {
			setLoading(true);
			const body = { filter };

			const res = await fastapiVmInstance.post(`/api/summary-report/summary-monthwise-business-metrices`, {filter});

			if (res.data.status === "error") throw new Error(res.data?.message);
			const result = metricsDataAdapter(res.data.result);
			setResData(result);
			setLoading(false);
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({
				message: parseErrMsgFromError(error),
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		}
	};

	useEffect(() => {
		fetch();
	}, [filter]);

	useEffect(() => {
		if (!resData?.length) return;

		const innerWidth = width - margin.left - margin.right;
		const innerHeight = height - margin.top - margin.bottom;

		const svg = d3.select(svgRef.current);
		svg.selectAll("*").remove();

		const chart = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);	

		const legendData = [
			{ label: "TY Rev", color: tyRevColor, type: "rect" },
			{ label: "LY Rev", color: lyRevColor, type: "rect" },
			{ label: "UPT", color: uptColor, type: "line" },
			{ label: "ASP", color: aspColor, type: "line" },
			{ label: "AOV", color: aovColor, type: "line" }
		];
		const legend = chart.append("g").attr("class", "legend");

		// Create items
		const legendGroups = legend
		.selectAll(".legend-item")
		.data(legendData)
		.enter()
		.append("g")
		.attr("class", "legend-item")
		.attr("transform", (d, i) => `translate(${i * 140}, 0)`); // horizontal spacing

		legendGroups
		.append("rect")
		.attr("width", 12)
		.attr("height", 12)
		.attr("x", -6)   // center align horizontally
		.attr("y", -6)   // center align vertically
		.attr("rx", 2)   // optional: slight rounded corners
		.attr("fill", (d) => d.color);

		// Text
		legendGroups
		.append("text")
		.attr("x", 12)
		.attr("dy", "0.32em")
		.style("font-size", "var(--font-size-body4)")
		.text((d) => d.label);

		// -------- AUTO-POSITION TO RIGHT ---------
		const legendWidth = legend.node().getBBox().width;

		// position it in the TOP-RIGHT (inside graph)
		legend.attr(
		"transform",
		`translate(${innerWidth - legendWidth}, -10)` // 👈 RIGHT ALIGN
		);

		const parseDate = d3.timeParse("%Y-%m-%d");
		const formatMonth = d3.timeFormat("%b");
		const fData = resData
			.map((d) => ({
				date: parseDate(d.date),
				upt: +d.TY__UPT,
				asp: +d.TY__ASP,
				aov: +d.TY__AOV,
				ly_upt: +d.LY__UPT,
				ly_asp: +d.LY__ASP,
				ly_aov: +d.LY__AOV,
				thisYear: +d.TY__SALE,
				lastYear: +d.LY__SALE,
			}))
			.sort((a, b) => a.date - b.date);
		// console.log(fData, "asndmasd");
		setFormattedData(fData);

		// --- X Scale ---
		// Extract unique sorted months actually present in data
		const availableMonths = Array.from(
			new Set(fData.map((d) => +d.date)) // unique timestamps
		)
			.sort((a, b) => a - b)
			.map((d) => new Date(d));

		// Force scale to fill the entire width evenly
		const x = d3
			.scalePoint()
			.domain(availableMonths)
			.range([0, innerWidth]) // innerWidth = width - margin.left - margin.right
			.padding(0.5); // adds small left-right margin, prevents cutoff

		setXScale(() => x);

		// --- Y Scales ---
		const yAreaScaleTy = d3
			.scaleLinear()
			.domain([0, d3.max(fData, (d) => Math.max(d.thisYear, d.lastYear)) * 1.2])
			.range([innerHeight, innerHeight * 0.3]);
		const yAreaScaleLy = d3
			.scaleLinear()
			.domain([0, d3.max(fData, (d) => Math.max(d.thisYear, d.lastYear)) * 1.2])
			.range([innerHeight, innerHeight * 0.4]);

		const yScales = {
			upt: d3
				.scaleLinear()
				.domain([0, d3.max(fData, (d) => d.upt) * 1.1])
				.range([innerHeight * 0.95, 0]),
			asp: d3
				.scaleLinear()
				.domain([0, d3.max(fData, (d) => d.asp) * 1.1])
				.range([innerHeight * 0.95, 0]),
			aov: d3
				.scaleLinear()
				.domain([0, d3.max(fData, (d) => d.aov) * 1.1])
				.range([innerHeight * 0.95, 0]),
		};

		// --- Gridlines ---
		const months = fData.map((d) => d.date);
		const quarterMonths = ["Apr", "Jul", "Oct", "Jan"];
		const quarterDates = fData.filter((d) => quarterMonths.includes(formatMonth(d.date)));

		// Monthly;
		chart
			.selectAll(".month-line")
			.data(months)
			.enter()
			.append("line")
			.attr("x1", (d) => x(d))
			.attr("x2", (d) => x(d))
			.attr("y1", 0)
			.attr("y2", innerHeight + parameters.length * 40)
			.attr("stroke", "#e2dfdfff")
			.attr("stroke-dasharray", "4 4");

		// --- Area graphs ---
		const areaThisYear = d3
			.area()
			.x((d) => x(d.date))
			.y0(innerHeight)
			.y1((d) => yAreaScaleTy(d.thisYear));

		const areaLastYear = d3
			.area()
			.x((d) => x(d.date))
			.y0(innerHeight)
			.y1((d) => yAreaScaleLy(d.lastYear));

		chart.append("path").datum(fData).attr("fill", tyRevColor).attr("d", areaThisYear);
		chart.append("path").datum(fData).attr("fill", lyRevColor).attr("d", areaLastYear);

		// Compute totals
		const totalTY = d3.sum(fData, d => d.thisYear);
		const totalLY = d3.sum(fData, d => d.lastYear);
		const avglAov = d3.mean(fData, d => d.aov);
		const avgAsp = d3.mean(fData, d => d.asp);
		const avgUpt = d3.mean(fData, d => d.upt);
		const ly_avglAov = d3.mean(fData, d => d.ly_aov);
		const ly_avgAsp = d3.mean(fData, d => d.ly_asp);
		const ly_avgUpt = d3.mean(fData, d => d.ly_upt);

		// X-position = last month
		// const lastX = x(fData[fData.length - 1].date);
		const lastPoint = fData[fData.length - 1];
		const lastXLine = x(lastPoint.date)


		const addEndLabel = (xScale, yScale, color, value, type, growth) => {
			chart.append("rect")
			.attr("x", xScale + 15)
			.attr("y", yScale - 7)
			.attr("width", 12)
			.attr("height", 12)
			.attr("fill", color);

			type != "ly_rev" && chart.append("text")
				.attr("x", xScale + 45)
				.attr("y", yScale - 10)
				.attr("dominant-baseline", "middle")
				.style("font-size", "var(--font-size-body3)")
				.style("font-weight", "600")
				.attr("fill", growth >= 0 ? "var(--color-growth)" : "var(--color-degrowth)")
				.text(`${growth}% ${growth >= 0 ? "▲" : "▼"}`);
			chart.append("text")
				.attr("x", xScale + 45)
				.attr("y", yScale + (type != "ly_rev" ? 10 : 0))
				.attr("dominant-baseline", "middle")
				.style("font-size", "var(--font-size-body3)")
				.style("font-weight", "600")
				.attr("fill", "#000000ff")
				.text(`${formatFixedOneNotation((value).toFixed(2), [ "aov", "asp", "upt"].includes(type) ? "" : "value")}`);
		};
		// TY / LY labels for areas
		addEndLabel(lastXLine, yAreaScaleTy(lastPoint.thisYear) - 20, tyRevColor, totalTY, "ty_rev", calculateGrowth(totalLY, totalTY));
		addEndLabel(lastXLine, yAreaScaleLy(lastPoint.lastYear) - 5, lyRevColor, totalLY, "ly_rev");

		// ASP / AOP/ UPT Lines
		addEndLabel(lastXLine, yScales.aov(lastPoint.aov) - 5, aovColor, avglAov, "aov", calculateGrowth(ly_avglAov, avglAov))
		addEndLabel(lastXLine, yScales.upt(lastPoint.upt) - 5, uptColor, avgUpt, "upt", calculateGrowth(ly_avgUpt, avgUpt))
		addEndLabel(lastXLine, yScales.asp(lastPoint.asp) - 5, aspColor, avgAsp, "asp", calculateGrowth(ly_avgAsp, avgAsp))

		const areaTooltip = d3
			.select("body")
			.append("div")
			.attr("class", "d3-tooltip")
			.style("position", "absolute")
			.style("background", "var(--color-tooltip-background)")
			// .style("border", "1px solid #ccc")
			.style("padding", "5px 10px")
			.style("border-radius", "4px")
			.style("font-size", "var(--font-size-tooltip)")
			.style("pointer-events", "none")
			.style("opacity", 0);

		// --- Dots on areas ---
		chart
			.selectAll(".dot-this-year")
			.data(fData)
			.enter()
			.append("circle")
			.attr("cx", (d) => x(d.date))
			.attr("cy", (d) => yAreaScaleTy(d.thisYear))
			.attr("r", 4)
			.attr("fill", "#00000000")
			// .attr("stroke", lightBlueColor)
			.attr("stroke-width", 1)
			.on("mouseover", function (event, d) {
				d3.select(this).attr("fill", tyRevColor);
				areaTooltip
					.style("opacity", 1)
					.html(`<span>TY REV</span><br/>${d3.timeFormat("%b")(d.date)}: ${formatFixedOneNotation(d["thisYear"], "value")}`)
					.style("left", event.pageX + 10 + "px")
					.style("top", event.pageY - 30 + "px");
			})
			.on("mouseout", function () {
				d3.select(this).attr("fill", "#00000000");
				areaTooltip.style("opacity", 0);
			});

		chart
			.selectAll(".dot-last-year")
			.data(fData)
			.enter()
			.append("circle")
			.attr("cx", (d) => x(d.date))
			.attr("cy", (d) => yAreaScaleLy(d.lastYear))
			.attr("r", 4)
			.attr("fill", "#00000000")
			// .attr("stroke", "white")
			.attr("stroke-width", 1.5)
			.on("mouseover", function (event, d) {
				d3.select(this).attr("fill", lyRevColor);
				areaTooltip
					.style("opacity", 1)
					.html(`<span>LY REV</span><br/>${d3.timeFormat("%b")(d.date)}: ${formatFixedOneNotation(d["lastYear"], "value")}`)
					.style("left", event.pageX + 10 + "px")
					.style("top", event.pageY - 30 + "px");
			})
			.on("mouseout", function () {
				d3.select(this).attr("fill", "#00000000");
				areaTooltip.style("opacity", 0);
			});

		// --- Lines for Metrics (Multiple Y-Axes) ---
		const metrics = [
			{ key: "upt", color: uptColor },
			{ key: "asp", color: aspColor },
			{ key: "aov", color: aovColor },
		];

		const tooltip = d3
			.select("body")
			.append("div")
			.attr("class", "d3-tooltip")
			.style("position", "absolute")
			.style("background", "var(--color-tooltip-background)")
			// .style("border", "1px solid #ccc")
			.style("padding", "5px 10px")
			.style("border-radius", "4px")
			.style("font-size", "var(--font-size-tooltip)")
			.style("pointer-events", "none")
			.style("opacity", 0);

		// Quarterly (bold dashed)
		chart
			.selectAll(".quarter-line")
			.data(quarterDates)
			.enter()
			.append("line")
			.attr("x1", (d) => x(d.date))
			.attr("x2", (d) => x(d.date))
			.attr("y1", 0)
			.attr("y2", innerHeight + parameters.length * 40)
			.attr("stroke", "#d4d1d1ff");

		metrics.forEach((m) => {
			const lineGen = d3
				.line()
				.x((d) => x(d.date))
				.y((d) => yScales[m.key](d[m.key]))
				.curve(d3.curveMonotoneX);

			chart.append("path").datum(fData).attr("fill", "none").attr("stroke", m.color).attr("stroke-width", 2).attr("d", lineGen);

			chart
				.selectAll(`.dot-${m.key}`)
				.data(fData)
				.enter()
				.append("circle")
				.attr("cx", (d) => x(d.date))
				.attr("cy", (d) => yScales[m.key](d[m.key]))
				.attr("r", 3.5)
				.attr("fill", m.color)
				.attr("stroke", "#fff")
				.attr("stroke-width", 1)
				.on("mouseover", function (event, d) {
					d3.select(this).attr("r", 6);
					tooltip
						.style("opacity", 1)
						.html(`<span>${m.key.toUpperCase()}</span><br/>${d3.timeFormat("%b")(d.date)}: ${d[m.key].toFixed(1)}`)
						.style("left", event.pageX + 10 + "px")
						.style("top", event.pageY - 30 + "px");
				})
				.on("mouseout", function () {
					d3.select(this).attr("r", 3.5);
					tooltip.style("opacity", 0);
				});
		});

		const fiscalQuarters = [
			{ name: "Q1", months: [3, 4, 5] }, // Apr, May, Jun
			{ name: "Q2", months: [6, 7, 8] }, // Jul, Aug, Sep
			{ name: "Q3", months: [9, 10, 11] }, // Oct, Nov, Dec
			{ name: "Q4", months: [0, 1, 2] }, // Jan, Feb, Mar (next year)
		];

		// Helper to compute the average x-gap dynamically
		const datesSorted = [...fData].sort((a, b) => a.date - b.date);
		let halfGap = 0;
		if (datesSorted.length > 1) {
			const first = x(datesSorted[0].date);
			const second = x(datesSorted[1].date);
			halfGap = (second - first) / 2;
		}

		fiscalQuarters.forEach((qtr) => {
			const quarterData = fData.filter((d) => qtr.months.includes(d.date.getMonth()));
			if (!quarterData.length) return;

			const middleMonth = qtr.months[1];
			const middleData = fData.find((d) => d.date.getMonth() === middleMonth);
			if (!middleData) return;

			const totalTY = d3.sum(quarterData, (d) => d.thisYear);
			const totalLY = d3.sum(quarterData, (d) => d.lastYear);
			const growth = ((totalTY - totalLY) / totalLY) * 100;

			// x midpoint + half the gap between two months
			const xMid = x(middleData.date) + halfGap;

			// chart
			// 	.append("text")
			// 	.attr("x", xMid)
			// 	.attr("y", innerHeight - 40)
			// 	.attr("text-anchor", "middle")
			// 	.attr("dominant-baseline", "central")
			// 	.attr("fill", "#333")
			// 	.style("font-size", "13px")
			// 	.style("font-weight", "600")
			// 	.text(qtr.name);
			// const color = growth >= 0 ? growthGreenColor : midOrangeColor;
			const color = growth >= 0 ? "var(--color-growth)" : "var(--color-degrowth)";
			const isUp = growth >= 0;

			// Text label for % value
			chart
				.append("text")
				.attr("x", xMid)
				.attr("y", innerHeight - 35)
				.attr("text-anchor", "middle")
				.attr("dominant-baseline", "central")
				.style("font-size", "18px")
				.style("font-weight", "600")
				.attr("fill", "#fff")
				.text(`(${formatFixedOneNotation(totalTY, "value")})`);

			// Growth % text (colored)
			chart
				.append("text")
				.attr("x", xMid) // slight offset to the right of revenue text
				.attr("y", innerHeight - 65)
				.attr("text-anchor", "middle")
				.attr("dominant-baseline", "central")
				.style("font-size", "22px")
				.style("font-weight", "600")
				.attr("fill", "#fff")
				.text(`${growth.toFixed(1)}%`);

			// Triangle marker beside growth %
			const size = 15;
			const trianglePath = isUp
				? `M${xMid + 48},${innerHeight - 68} l${-size / 1.2},${size / 1.2}   l${(size / 1.2) * 2},0 Z` // ▲ up
				: `M${xMid + 48},${innerHeight - 60} l${-size / 1.2},${-size / 1.2}  l${(size / 1.2) * 2},0 Z`; // ▼ down
			chart.append("path").attr("d", trianglePath).attr("fill", color);

			// // Growth part (colored)
			// text.append("tspan")
			// 	.attr("fill", color)
			// 	.text(`(${growth.toFixed(1)}%)`);
		});

		return () => tooltip.remove();
	}, [resData, width, height]);

	const tooltipDataShow = (name,key, ty_value, ly_value, growth) =>{
		const growthColor = (key === "DISCOUNT_PERCENT") ? growth > 0 ? "red": "green" : growth > 0 ? "green" : "red"
		return(
			<Stack>
				<Typography
				 sx={{ fontSize:"var(--font-size-tooltip)", marginRight: "2px"}}>{name}</Typography>
			  <Stack display={"flex"} flexDirection={"row"}>
				<Typography
				 sx={{ fontSize:"var(--font-size-tooltip)", marginRight: "2px"}}>{`TY: `}</Typography>
				<Typography sx={{ fontSize:"var(--font-size-tooltip)", marginRight: "3px"}}>{`${ty_value} `}</Typography>
				<Typography sx={{ fontSize:"var(--font-size-tooltip)", color: growthColor}}>{` (${growth})`}</Typography>
			  </Stack>
			  <Stack display={"flex"} flexDirection={"row"}>
				<Typography
				 sx={{ fontSize:"var(--font-size-tooltip)", marginRight: "2px"}}>{`LY: `}</Typography>
				<Typography sx={{ fontSize:"var(--font-size-tooltip)"}}>{ly_value}</Typography>
			  </Stack>

			</Stack>
		  
		)
	  }

	if (loading) return <CardSkeleton />;
	else
		return (
			<Box sx={{ display: "flex", flexWrap: "wrap", p: 2, zoom: 0.7 }}>
				<Typography fontSize={"var(--font-size-h1)"}>
					Monthwise Business Metrics
				</Typography>
				<Box>
					<svg ref={svgRef} width={width} height={height}></svg>

					{/* Table Aligned to X-Axis */}
					{xScale && (
						<Box sx={{ display: "flex", mt: 2, width }}>
							{/* Fixed Parameter Column */}
							<Box
								sx={{
									width: 110,
									// borderTop: (theme) => `1px solid ${theme.palette.divider}`,
									// borderLeft: (theme) => `1px solid ${theme.palette.divider}`,
								}}>
								<Box
									sx={{
										borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
										// background: "#f5f5f5",
										textAlign: "center",
										fontWeight: 600,
										py: 0.5,
										height: "30px"
									}}>
									KPI
								</Box>
								{Object.values(tableRowTypes).map((param) => (
									<Box
										key={param.key}
										sx={{
											borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
											// background: "#f5f5f5",
											textAlign: "center",
											fontWeight: 600,
											py: 0.5,
											height: "21px"
										}}>
										<Typography width="100%" noWrap variant="body2">
											{param.name}
										</Typography>
									</Box>
								))}
							</Box>

							{/* Right Side: Table perfectly aligned with x-axis */}
							<Box
								sx={{
									width: width - margin.right - margin.left,
									borderLeft: (theme) => `1px solid ${theme.palette.divider}`,
									// borderTop: (theme) => `1px solid ${theme.palette.divider}`,
									ml: `${margin.left - 110}px`, // align to chart’s plot area
								}}>
								{/* Month Header */}
								<Box
									sx={{
										display: "grid",
										gridTemplateColumns: `repeat(${formattedData.length}, 1fr)`,
									}}>
									{formattedData.map((d, i) => (
										<Box
											key={`month-${i}`}
											sx={{
												// borderRight: (theme) => `1px solid ${theme.palette.divider}`,
												borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
												// background: "#f5f5f5",
												textAlign: "center",
												fontWeight: 600,
												py: 0.5,
												height: "30px"
											}}>
											{d3.timeFormat("%b")(d.date)}
										</Box>
									))}
								</Box>

								{/* Parameter Rows */}
								{Object.values(tableRowTypes).map((column) => (
									<Box
										key={column.key}
										sx={{
											display: "grid",
											gridTemplateColumns: `repeat(${formattedData.length}, 1fr)`,
										}}>
										{resData.map((d, i) =>{
											const growth = calculateGrowth(d[`LY__${column.key}`],d[`TY__${column.key}`])
											return (
											<Box
												key={`${column.key}-${i}`}
												sx={{
													// borderRight: (theme) => `1px solid ${theme.palette.divider}`,
													borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
													textAlign: "center",
													py: 0.5,
													background: i > 0 ? (resData[i - 1][`TY__${column.key}`] - d[`TY__${column.key}`] < 0) ? "#fff" : "var(--color-degrowth2)" : "#fff",
													color: grey[900],
													fontWeight: 600,
													height: "21px",
													cursor: "default"
												}}>
													<Tooltip title={tooltipDataShow(column.name,column.key, column.valueGetter(d[`TY__${column.key}`]), column.valueGetter(d[`LY__${column.key}`]), growth)}
													slotProps={{
														tooltip: {
														sx: {
															backgroundColor: "var(--color-tooltip-background)",
															color: "var(--color-tooltip)",
															// border: "0.2px solid black",
														}
														}
													}} followCursor>
														{column.valueGetter(d[`TY__${column.key}`]) ?? "-"}
													</Tooltip>
											</Box>
										)
										})}
									</Box>
								))}
							</Box>
						</Box>
					)}
				</Box>
			</Box>
		);
};

export default MonthwiseBusinessMetrics;
